/**
 * AccentSwitch — Accent color selector dropdown
 *
 * **100% DSL-driven**: Accent options come from LayoutConfig.accentOptions.
 * Zero hardcoded color list.
 */

import { useState, useRef, useEffect } from 'react';
import { Check, Palette } from 'lucide-react';
import { useAppStore, type AccentColor } from '@/stores/app-store';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useLayoutConfigContext } from '@/lib/use-layout-config';
import { VISUAL_PATTERN_OPTIONS, type VisualPatternId } from '@/lib/portal-theme';
import { BooxCiMark } from '@/components/brand/BooxCiMark';
import {
  BOOX_CI_SET_OPTIONS,
  BOOX_ICON_SET_OPTIONS,
  type BooxCiSetId,
  type BooxIconSetId,
} from '@/lib/visual-preferences';

export function AccentSwitch() {
  const {
    accent,
    setAccent,
    pwaVisualPattern,
    setPwaVisualPattern,
    pwaCiSet,
    setPwaCiSet,
    pwaIconSet,
    setPwaIconSet,
  } = useAppStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { config } = useLayoutConfigContext();
  const accentOptions = config.accentOptions;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => setOpen(!open)}
        title={t('accent.label')}
      >
        <Palette className="h-4 w-4" style={{ color: 'var(--category-indicator, var(--color-muted-foreground))' }} />
      </Button>

      {open && (
        <div className="absolute right-0 top-10 z-50 max-h-[min(80vh,620px)] w-[340px] overflow-y-auto rounded-md border bg-popover p-3 shadow-md">
          <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('accent.label')}
          </p>
          <div className="grid grid-cols-4 gap-1.5">
            {accentOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setAccent(opt.id as AccentColor)}
                title={opt.label}
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full transition-all',
                  opt.swatch,
                  accent === opt.id
                    ? 'ring-2 ring-offset-2 ring-offset-popover ring-foreground/40'
                    : 'hover:scale-110',
                )}
              >
                {accent === opt.id && <Check className="h-3 w-3 text-white" />}
              </button>
            ))}
          </div>
          <div className="mt-3 border-t pt-3">
            <label className="mb-1.5 block px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Background Pattern
            </label>
            <select
              value={pwaVisualPattern}
              onChange={(event) => setPwaVisualPattern(event.target.value as VisualPatternId)}
              className="w-full rounded-md border bg-background px-2.5 py-2 text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/30"
            >
              {VISUAL_PATTERN_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1.5 px-1 text-[11px] leading-snug text-muted-foreground">
              Used by the PWA menu panel background.
            </p>
          </div>
          <div className="mt-3 border-t pt-3">
            <label className="mb-1.5 block px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              CI set
            </label>
            <div className="grid grid-cols-[44px_1fr] gap-2">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg border bg-background">
                <BooxCiMark ciSet={pwaCiSet} className="h-9 w-9" />
              </div>
              <select
                value={pwaCiSet}
                onChange={(event) => setPwaCiSet(event.target.value as BooxCiSetId)}
                className="w-full rounded-md border bg-background px-2.5 py-2 text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/30"
              >
                {BOOX_CI_SET_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <p className="mt-1.5 px-1 text-[11px] leading-snug text-muted-foreground">
              Controls Boox mascot/persona marks for menu panel, assistant and checklist surfaces.
            </p>
          </div>
          <div className="mt-3 border-t pt-3">
            <label className="mb-1.5 block px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Icon set
            </label>
            <select
              value={pwaIconSet}
              onChange={(event) => setPwaIconSet(event.target.value as BooxIconSetId)}
              className="w-full rounded-md border bg-background px-2.5 py-2 text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/30"
            >
              {BOOX_ICON_SET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1.5 px-1 text-[11px] leading-snug text-muted-foreground">
              Global icon provider preference. Lucide is active now; Tabler, Phosphor and Streamline Free are registered as provider options.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
